import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Textarea,
} from '@roger/ui-components';
import { bulkAppendNotes, updateTaskNotes, client } from '../../services/api';
import { useToast } from '../../hooks/useToastContext';

interface CommentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderIds: string[];
  /** When editing a single order, pass the current notes to pre-fill */
  initialNotes?: string;
  /** 'single' overwrites via PUT, 'bulk' appends via POST */
  mode: 'single' | 'bulk';
  onSuccess: () => void;
}

const CommentDialog: React.FC<CommentDialogProps> = ({
  open,
  onOpenChange,
  orderIds,
  initialNotes,
  mode,
  onSuccess,
}) => {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { error: showError } = useToast();
  const count = orderIds.length;

  useEffect(() => {
    if (open) {
      setComment(mode === 'single' ? (initialNotes ?? '') : '');
    }
  }, [open, mode, initialNotes]);

  const handleSubmit = async () => {
    if (orderIds.length === 0) return;
    setIsSubmitting(true);
    try {
      if (mode === 'single') {
        await updateTaskNotes({
          path: { task_id: orderIds[0] },
          body: { notes: comment.trim() || null },
          client,
        });
      } else {
        if (!comment.trim()) return;
        const { data } = await bulkAppendNotes({
          body: { order_ids: orderIds, comment: comment.trim() },
          client,
        });
        if (data?.failed && data.failed.length > 0) {
          showError(
            `Failed to add comment to ${data.failed.length} of ${count} orders`
          );
        }
      }
      setComment('');
      onOpenChange(false);
      onSuccess();
    } catch {
      showError('Failed to save comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const title =
    mode === 'single' ? 'Edit Comment' : `Add Comment to ${count} Orders`;

  const buttonLabel = mode === 'single' ? 'Save' : 'Add Comment';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder='Enter comment...'
          rows={4}
        />
        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || (mode === 'bulk' && !comment.trim())}
          >
            {isSubmitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            {buttonLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CommentDialog;
